import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Card, CardBody, CardHeader, CardTitle, Spinner, Table } from "reactstrap";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { BarChart2, Edit2, Pause, Play, Square } from "react-feather";

import { apiFetch, API } from "../../services/api";
import ExperimentModal from "../../components/modals/ExperimentModal";
import {
    statusLabel,
    statusColor,
    surfaceLabel,
    verdictLabel,
    concludeWarning,
    concludeWarningTone,
    apiErrorMessage,
} from "../../helpers/experiments";
import { commissionSummary } from "../../helpers/experimentResults";

const fmtDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    // Les dates non renseignées reviennent en zéro Go ("0001-01-01T00:00:00Z").
    if (isNaN(date.getTime()) || date.getFullYear() < 1971) return "—";
    return date.toLocaleDateString("fr-FR");
};

// Modale de conclusion : choisir le gagnant et voir CE QUE ÇA FAIT. Trois cas,
// aux conséquences différentes, qu'il faut lire avant de valider :
//   - contrôle A (ou variante sans levier) : rien n'est copié, l'expérience
//     s'arrête et le site revient aux défauts de la surface ;
//   - variante en données : sa config est copiée dans les défauts de la surface ;
//   - variante codée (`variantKey`) : servie à 100 % sans cache CDN jusqu'au
//     déploiement du front.
function ConcludeExperimentModal({ isOpen, closeModal, experiment, onDone }) {
    const [winner, setWinner] = useState(experiment.winner || "A");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const variant = (experiment.variants || []).find((v) => v.key === winner);

    const confirm = () => {
        setSaving(true);
        setError(null);
        apiFetch(API.back + "/experiment-status", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: experiment.id, action: "conclude", winner }),
        })
            .then((res) => {
                if (!res.ok) {
                    return res.text().then((body) => Promise.reject(new Error(apiErrorMessage(res.status, body))));
                }
                setSaving(false);
                onDone();
            })
            .catch((err) => {
                console.log(err);
                setSaving(false);
                setError("Conclusion impossible : " + err.message);
            });
    };

    return (
        <Modal show={isOpen} onHide={closeModal} centered>
            <Modal.Header closeButton>
                <Modal.Title>Conclure « {experiment.name} »</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error ? <div className="alert alert-danger">{error}</div> : null}
                <Form.Group>
                    <Form.Label>Variante gagnante</Form.Label>
                    {(experiment.variants || []).map((v) => (
                        <Form.Check
                            key={v.key}
                            type="radio"
                            id={"winner-" + v.key}
                            name="winner"
                            label={v.key + " — " + (v.name || "") + (v.key === "A" ? " (contrôle)" : "")}
                            checked={winner === v.key}
                            onChange={() => setWinner(v.key)}
                        />
                    ))}
                </Form.Group>
                {variant ? (
                    <div className={"alert alert-" + concludeWarningTone(variant)}>{concludeWarning(variant)}</div>
                ) : null}
                <p className="text-muted small mb-0">
                    L'expérience passe en « Conclue » et n'est plus servie comme test. Le choix se relit dans le journal.
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal} disabled={saving}>
                    Annuler
                </Button>
                <Button variant="primary" onClick={confirm} disabled={saving}>
                    {saving ? "En cours…" : "Conclure"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export function ExperimentsList() {
    const [experiments, setExperiments] = useState(null);
    const [latestByID, setLatestByID] = useState({});
    const [error, setError] = useState(null);
    const [edited, setEdited] = useState(null);
    const [concluded, setConcluded] = useState(null);
    const [busyID, setBusyID] = useState(null);

    const load = useCallback(() => {
        setError(null);
        // Registre non internationalisé : une expérience porte son ciblage de
        // marché, elle ne dépend pas du marché sélectionné dans le back-office.
        apiFetch(API.back + "/experiments", { method: "GET", market: false })
            .then((res) => {
                if (!res.ok) throw new Error("HTTP " + res.status);
                return res.json();
            })
            .then((data) => {
                const list = Array.isArray(data) ? data : [];
                setExperiments(list);
                // Dernière ligne d'analyse par expérience : exposés et verdict de
                // la liste. Une expérience jamais analysée n'a pas de ligne.
                list
                    .filter((experiment) => experiment.status !== "draft")
                    .forEach((experiment) => {
                        apiFetch(API.back + "/experiment-results?id=" + encodeURIComponent(experiment.id), {
                            method: "GET",
                            market: false,
                        })
                            .then((res) => (res.ok ? res.json() : []))
                            .then((rows) => {
                                if (Array.isArray(rows) && rows.length > 0) {
                                    setLatestByID((current) => ({ ...current, [experiment.id]: rows[0] }));
                                }
                            })
                            .catch((err) => console.log(err));
                    });
            })
            .catch((err) => {
                console.log(err);
                setError("Expériences indisponibles (" + err.message + ") — vérifier la connexion au back-office.");
                setExperiments([]);
            });
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const changeStatus = (experiment, action) => {
        setBusyID(experiment.id);
        apiFetch(API.back + "/experiment-status", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: experiment.id, action, winner: "" }),
        })
            .then((res) => {
                if (!res.ok) {
                    return res.text().then((body) => Promise.reject(new Error(apiErrorMessage(res.status, body))));
                }
                setBusyID(null);
                load();
            })
            .catch((err) => {
                console.log(err);
                setBusyID(null);
                alert("Action impossible : " + err.message);
            });
    };

    const exposedCell = (experiment) => {
        const row = latestByID[experiment.id];
        if (!row || !Array.isArray(row.variants) || row.variants.length === 0) return "—";
        return row.variants.map((v) => v.key + " : " + (v.exposed || 0)).join(" / ");
    };

    // Commission par visiteur : lecture humaine, à côté du verdict du moteur (clics).
    const commissionCell = (experiment) => {
        const summary = commissionSummary(latestByID[experiment.id]);
        if (!summary) return "—";
        return (
            <span title={summary.buyers}>
                <div style={{ whiteSpace: "nowrap" }}>{summary.perVariant}</div>
                {summary.probText || summary.reading ? (
                    <div className="text-muted small">
                        {summary.probText}
                        {summary.probText && summary.reading ? " · " : ""}
                        {summary.reading}
                    </div>
                ) : null}
            </span>
        );
    };

    const verdictCell = (experiment) => {
        const row = latestByID[experiment.id];
        if (!row || !row.verdict) return "—";
        return (
            <span title={row.verdict.reason || ""}>
                {verdictLabel(row.verdict.decision)}
                {row.verdict.winner ? " (" + row.verdict.winner + ")" : ""}
            </span>
        );
    };

    return (
        <Card className="flex-fill w-100">
            <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                    <CardTitle tag="h5" className="mb-0">
                        A/B Testing
                    </CardTitle>
                    <div>
                        <Link to="/experiments/surface-config" className="btn btn-outline-secondary btn-sm mr-2">
                            Défauts de surface
                        </Link>
                        <Link to="/experiments/journal" className="btn btn-outline-secondary btn-sm mr-2">
                            Journal
                        </Link>
                        <Button variant="secondary" size="sm" onClick={() => setEdited({})}>
                            Nouvelle expérience
                        </Button>
                    </div>
                </div>
                <div className="text-muted small mt-2">
                    Une seule expérience est servie par surface. Décision hebdomadaire : ne rien conclure avant la durée minimale.
                </div>
            </CardHeader>
            <CardBody>
                {error ? <div className="alert alert-danger">{error}</div> : null}
                {!experiments && !error ? (
                    <div className="d-flex align-items-center">
                        <Spinner color="primary" size="sm" />
                        <span className="text-muted ml-2">Chargement…</span>
                    </div>
                ) : null}
                {experiments && experiments.length === 0 && !error ? (
                    <p className="text-muted mb-0">Aucune expérience pour le moment.</p>
                ) : null}
                {experiments && experiments.length > 0 ? (
                    <div style={{ overflowX: "auto" }}>
                        <Table responsive size="sm" className="mb-0">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Surface</th>
                                    <th>Statut</th>
                                    <th>Début</th>
                                    <th>Fin</th>
                                    <th>Exposés</th>
                                    <th>Commission / visiteur</th>
                                    <th>Verdict (clics)</th>
                                    <th colSpan={4} />
                                </tr>
                            </thead>
                            <tbody>
                                {experiments.map((experiment) => (
                                    <tr key={experiment.id}>
                                        <td>
                                            <div>{experiment.name}</div>
                                            <div className="text-muted small">{experiment.hypothesis}</div>
                                        </td>
                                        <td>{surfaceLabel(experiment.surface)}</td>
                                        <td>
                                            <Badge color={statusColor(experiment.status)}>{statusLabel(experiment.status)}</Badge>
                                            {experiment.status === "concluded" && experiment.winnerApplied ? (
                                                <div className="text-muted small">appliqué à la surface</div>
                                            ) : null}
                                        </td>
                                        <td>{fmtDate(experiment.startedAt)}</td>
                                        <td>{fmtDate(experiment.endedAt)}</td>
                                        <td>{exposedCell(experiment)}</td>
                                        <td>{commissionCell(experiment)}</td>
                                        <td>{verdictCell(experiment)}</td>
                                        <td className="table-action">
                                            <Link to={"/experiments/results/" + experiment.id} title="Voir les résultats">
                                                <BarChart2 size={18} className="align-middle" />
                                            </Link>
                                        </td>
                                        <td className="table-action" style={{ cursor: "pointer" }}>
                                            <Edit2
                                                size={18}
                                                className="align-middle"
                                                title="Modifier"
                                                onClick={() => setEdited(experiment)}
                                            />
                                        </td>
                                        <td className="table-action" style={{ cursor: "pointer" }}>
                                            {experiment.status === "draft" || experiment.status === "paused" ? (
                                                <Play
                                                    size={18}
                                                    className="align-middle"
                                                    title={experiment.status === "paused" ? "Reprendre" : "Démarrer"}
                                                    onClick={() => busyID !== experiment.id && changeStatus(experiment, "start")}
                                                />
                                            ) : null}
                                            {experiment.status === "running" ? (
                                                <Pause
                                                    size={18}
                                                    className="align-middle"
                                                    title="Mettre en pause"
                                                    onClick={() => busyID !== experiment.id && changeStatus(experiment, "pause")}
                                                />
                                            ) : null}
                                        </td>
                                        <td className="table-action" style={{ cursor: "pointer" }}>
                                            {experiment.status === "running" || experiment.status === "paused" ? (
                                                <Square
                                                    size={18}
                                                    className="align-middle"
                                                    title="Conclure"
                                                    onClick={() => setConcluded(experiment)}
                                                />
                                            ) : null}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                ) : null}
            </CardBody>

            {edited ? (
                <ExperimentModal
                    isOpen={true}
                    closeModal={() => setEdited(null)}
                    selectedExperiment={edited}
                    onSaved={() => {
                        setEdited(null);
                        load();
                    }}
                />
            ) : null}

            {concluded ? (
                <ConcludeExperimentModal
                    isOpen={true}
                    closeModal={() => setConcluded(null)}
                    experiment={concluded}
                    onDone={() => {
                        setConcluded(null);
                        load();
                    }}
                />
            ) : null}
        </Card>
    );
}

export default ExperimentsList;
